import { ArrowUpRight } from "lucide-react";

import { SectionHeading } from "@/components/site/Blocks";
import { JYNTIX_PRODUCTS, JYNTIX_URL } from "@/lib/jyntix";

const COPY = {
  en: {
    eyebrow: "Recommended product",
    title: (name: string) => `Software recommendation: ${name} by JYNTIX`,
    lead: "JYNTIX is an OXORY brand. Where a project needs product software rather than consulting days, we recommend and implement the matching JYNTIX product.",
    open: (name: string) => `Open ${name} on jyntix.net`,
    brand: "All JYNTIX products",
    newTab: "opens in a new tab",
  },
  tr: {
    eyebrow: "Önerilen ürün",
    title: (name: string) => `Yazılım önerisi: JYNTIX ${name}`,
    lead: "JYNTIX bir OXORY markasıdır. Bir projede danışmanlık gününden çok ürün yazılımı gerektiğinde ilgili JYNTIX ürününü öneriyor ve devreye alıyoruz.",
    open: (name: string) => `${name} ürününü jyntix.net üzerinde açın`,
    brand: "Tüm JYNTIX ürünleri",
    newTab: "yeni sekmede açılır",
  },
} as const;

/**
 * Editorial product recommendation that links out to the matching JYNTIX
 * product page. Deliberately a normal followed link — JYNTIX is a sister
 * brand we want search engines to associate with these solution pages.
 */
export function JyntixRecommendation({
  product,
  locale = "en",
}: {
  product: string;
  locale?: "en" | "tr";
}) {
  const entry = JYNTIX_PRODUCTS.find((item) => item.name === product);
  if (!entry) return null;
  const t = COPY[locale];

  return (
    <section className="bg-secondary">
      <div className="section-shell py-20">
        <SectionHeading eyebrow={t.eyebrow} title={t.title(entry.name)} lead={t.lead} />
        <p className="mt-6 max-w-2xl text-muted-foreground">
          {locale === "tr" ? entry.descriptionTr : entry.descriptionEn}
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a
            href={entry.url}
            target="_blank"
            rel="noopener"
            aria-label={`${t.open(entry.name)} (${t.newTab})`}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-3 text-sm font-bold text-primary-foreground"
          >
            {t.open(entry.name)} <ArrowUpRight className="size-4" aria-hidden="true" />
          </a>
          <a
            href={JYNTIX_URL}
            target="_blank"
            rel="noopener"
            aria-label={`${t.brand} (${t.newTab})`}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-background"
          >
            {t.brand} <ArrowUpRight className="size-4" aria-hidden="true" />
          </a>
        </div>
      </div>
    </section>
  );
}
